
import { defineComponent, ref, onMounted, onBeforeUnmount, reactive, watch, nextTick, getCurrentInstance } from '@vue/composition-api';
import { formatDateAgo } from '@/util/date-util';
import { checkIsGuestAccount } from '@/util/guest-util';
import moment from 'moment';

import TotalCommentsCnt from '@/entities/report-insights/comments/TotalCommentsCnt.vue';
import ModifyComment from '@/entities/report-insights/comments/ModifyComment.vue';
import LikeAndDislike from '@/entities/report-insights/comments/LikeAndDislike.vue';
import ShowFiles from '@/entities/report-insights/comments/ShowFiles.vue';
import ShowImages from '@/entities/report-insights/comments/ShowImages.vue';

const PUBLIC = 0;
const PRIVATE = 1;

export default defineComponent({
  name: 'ShowComment',
  components: {
    ShowImages,
    ShowFiles,
    LikeAndDislike,
    TotalCommentsCnt,
    ModifyComment,
  },
  props: {
    comments: {
      type: Array,
      required: true,
      default: () => [],
    },
    userId: {
      type: String,
      required: true,
      default: '',
    },
    ownerId: {
      type: String,
      required: true,
      default: '',
    },
    closeReply: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['onClickLike', 'initComment', 'selectFiles', 'onChangePrivate'],
  setup(props, { emit }) {
    const instance = getCurrentInstance();
    const vm = instance?.proxy;
    const t = vm?.$t ? vm.$t.bind(vm) : (msg: string) => msg;

    const replyIdx = ref<null | number>(null);
    const openChildCommentsIdx = ref<null | number>(null);
    const replyContent = ref('');

    const openMenuIdx = ref<number | null>(null);
    const openChildMenuIdx = ref<number | null>(null);
    const isModifyMode = ref<boolean>(false);
    const isSubModifyMode = ref<boolean>(false);
    const selectedModifyCommentId = ref<null | number>(null);

    const isEditMode = ref<boolean>(false);
    const isGuest = ref<boolean>(false);

    watch(
      () => props.closeReply,
      () => {
        replyIdx.value = null;
      }
    );

    onMounted(() => {
      isGuest.value = checkIsGuestAccount();

      detectOverflow();
      // Collapse 열림/닫힘 시에도 재검사
      document.addEventListener('shown.bs.collapse', detectOverflow);
      document.addEventListener('hidden.bs.collapse', detectOverflow);
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('click', handleClickSubOutSide);

      // Collapse 이벤트 리스너 해제
      document.removeEventListener('shown.bs.collapse', detectOverflow);
      document.removeEventListener('hidden.bs.collapse', detectOverflow);
    });

    const toggleMenu = (idx: number, e?: any) => {
      if (e) {
        e.stopPropagation();
      }

      openMenuIdx.value = idx;
    };

    const closeMenu = () => {
      openMenuIdx.value = null;
    };

    const toggleSubMenu = (idx: number, e: any) => {
      e.stopPropagation();
      openChildCommentsIdx.value = openChildCommentsIdx.value === idx ? null : idx;
    };

    const closeSubMenu = () => {
      openChildCommentsIdx.value = null;
    };

    // 바깥 클릭 시 닫기
    const handleClickOutside = event => {
      if (!event.target.closest('.menu-container')) {
        closeMenu();
      }
    };

    const handleClickSubOutSide = (e: any) => {
      if (!e.target.closest('.menu-sub-container')) {
        closeSubMenu();
      }
    };

    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('click', handleClickSubOutSide);
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('click', handleClickSubOutSide);
    });

    const edit = (comment: any) => {
      isModifyMode.value = true;
      selectedModifyCommentId.value = comment.id;
      openMenuIdx.value = comment.id;
      closeMenu();
    };

    const editSub = (comment: any) => {
      isSubModifyMode.value = true;
      selectedModifyCommentId.value = comment.id;
      closeSubMenu();
      // keep openChildCommentsIdx as-is
    };

    const remove = (comment: any) => {
      closeMenu();
      // alert('삭제 클릭!');

      emit('remove', comment);
    };

    const openReply = (idx: number) => {
      if (replyIdx.value === idx) {
        replyIdx.value = null;
      } else {
        replyIdx.value = idx;
      }

      emit('initComment');
    };

    const openChildComments = (idx: number) => {
      openChildCommentsIdx.value = idx;
    };

    const onClickLike = (params: any) => {
      emit('onClickLike', params);
    };

    const onClickDislike = (params: any) => {
      emit('onClickDislike', params);
    };

    const subCommentParams = reactive({
      parentId: null,
      title: '',
      content: null,
      isPrivate: 0,
      multipartFiles: [],
    });

    const subComment = (e: any) => {
      subCommentParams.content = e.target.value;
    };

    const submitReply = (comment: any) => {
      subCommentParams.parentId = comment.id;
      // alert('대댓글 전송: ' + replyContent.value);
      // replyContent.value = '';
      // replyIdx.value = null;
      emit('submitReply', subCommentParams);
      replyIdx.value = null;
    };

    const convertDate = (time: any) => {
      const date = moment(time).utc(true);
      return formatDateAgo(date);
    };

    const openPrivateComment = (comment: any) => {
      const { userId, ownerId } = props;

      const isPrivate = comment.isPrivate === PRIVATE;
      const isMasterReport = ownerId === comment.userId;
      const isCommentMaster = userId === comment.userId;

      return isPrivate && (isMasterReport || isCommentMaster);
    };

    // const subFileInputRef = ref<HTMLInputElement | null>(null);
    // const subImageInputRef = ref<HTMLInputElement | null>(null);
    //
    // const triggerFileInput = (type: number) => {
    //   const IMAGE = 0;
    //   const FILE = 1;
    //
    //   if (subImageInputRef.value && type === IMAGE) {
    //     subImageInputRef.value[0].click();
    //   }
    //
    //   if (subFileInputRef.value && type === FILE) {
    //     subFileInputRef.value[0].click();
    //   }
    // };

    const onSelectFiles = (files: any | any[]) => {
      let filesArray: any[];

      if (Array.isArray(files)) {
        filesArray = files;
      } else if (typeof files === 'object' && files !== null && 'file' in files) {
        filesArray = [files['file']];
      } else {
        filesArray = [files];
      }

      if (filesArray && filesArray.length > 0) {
        filesArray.forEach(file => {
          const index = subCommentParams.multipartFiles.findIndex(
            (f: any) => f.name === file.name && f.size === file.size && f.lastModified === file.lastModified && f.type === file.type
          );

          if (index !== -1) {
            // 동일한 파일이 있으면 제거
            subCommentParams.multipartFiles.splice(index, 1);
          } else {
            // 동일한 파일이 없으면 추가
            subCommentParams.multipartFiles.push(file);
          }
        });
      }
    };

    const files = ref([]);
    const images = ref([]);

    const removeFile = (index: number) => {
      onSelectFiles(files.value[index]);
      files.value.splice(index, 1);
    };

    const removeImage = (index: number) => {
      onSelectFiles(images.value[index]);
      URL.revokeObjectURL(images.value[index].url);
      images.value.splice(index, 1);
    };

    const cancelEdit = () => {
      isModifyMode.value = false;
      isSubModifyMode.value = false;
      openMenuIdx.value = null;
      openChildCommentsIdx.value = null;
      closeMenu();
    };

    const expandedComments = ref<number>([]);
    const toggleExpand = (id: number, e?: any) => {
      if (e) {
        e.stopPropagation();
      }

      if (expandedComments.value.includes(id)) {
        const findIndex = expandedComments.value.findIndex((expandedComment: any) => expandedComment.id === id);
        expandedComments.value.splice(findIndex, 1);
      } else {
        expandedComments.value.push(id);
      }
    };
    const overflowComments = ref<number[]>([]);

    const detectOverflow = async () => {
      await nextTick();
      props.comments.forEach(comment => {
        const el = document.getElementById(`comment-text-${comment.id}`);

        if (el && el.scrollHeight > el.clientHeight) {
          overflowComments.value.push(comment.id);
        }

        comment.children.forEach((child: any) => {
          if (el && el.scrollHeight > el.clientHeight) {
            overflowComments.value.push(child.id);
          }
        });
      });
    };

    onMounted(() => {
      detectOverflow();
    });

    watch(
      () => props.comments,
      () => {
        detectOverflow();
      },
      { deep: true }
    );

    const getAllComments = (result: any) => {
      isModifyMode.value = false;
      isSubModifyMode.value = false;
      emit('getAllComments', result);
    };

    const isPrivateMode = ref<boolean>(false);
    const onChangePrivate = (e: any) => {
      isPrivateMode.value = e.target.checked;

      emit('onChangePrivate', isPrivateMode.value);
    };

    const sortMode = ref<boolean>(true);
    const onChangeSort = () => {
      sortMode.value = !sortMode.value;

      emit('onChangeSort', sortMode.value);
    };

    const repliesIdx = ref<null | number>(null);
    const onExpandReplies = (idx: number) => {
      if (repliesIdx.value === idx) {
        repliesIdx.value = null;
      } else {
        repliesIdx.value = idx;
      }

      emit('initComment');
    };

    return {
      images,
      files,
      PUBLIC,
      PRIVATE,
      isGuest,
      openMenuIdx,
      selectedModifyCommentId,
      replyIdx,
      replyContent,
      openChildCommentsIdx,
      checkIsGuestAccount,
      isEditMode,
      isModifyMode,
      isSubModifyMode,
      t,
      toggleMenu,
      toggleSubMenu,
      edit,
      editSub,
      remove,
      openReply,
      submitReply,
      openChildComments,
      onSelectFiles,
      onClickLike,
      onClickDislike,
      subComment,
      convertDate,
      openPrivateComment,
      removeFile,
      removeImage,
      cancelEdit,
      getAllComments,
      expandedComments,
      toggleExpand,
      overflowComments,
      onChangePrivate,
      sortMode,
      onChangeSort,
      repliesIdx,
      onExpandReplies,
    };
  },
});
